import { useVideoSubtitles } from '@/services/subtitles/useVideoSubtitles'
import type { Video } from '@/types/video'
import { TranslationOverlay } from './TranslationOverlay'

type VideoSubtitleOverlayProps = {
  debugName: string
  video?: Video | null
  /** The player's media position, in seconds. See `useVideoSubtitles`. */
  getCurrentTime: () => number | undefined
  /** False parks the cue loop — an off-screen feed card, or a closed fullscreen modal. */
  enabled?: boolean
  maxHeight?: string
  /** Placement, owned by the caller. */
  style?: Record<string, unknown>
}

/**
 * Subtitles over a video, in the viewer's language.
 *
 * A wrapper around `TranslationOverlay` rather than a branch inside it. The panel is shared
 * with lesson image captions, which are a static string and must keep a render path free of
 * a fetch, a query client and an animation-frame loop — and `isRtl` is not even the same
 * question on the two surfaces: for an image it is the page's `is_rtl` from the server, for a
 * subtitle it is a property of the track that was chosen, and the backend can serve an Arabic
 * track on a page whose `is_rtl` is false.
 *
 * The panel stays mounted between cues, showing nothing, so its toggle does not appear and
 * disappear through every pause in the speech. It renders nothing at all only when this
 * viewer gets no captions — still encoding, unsupported language, English, or a track that
 * failed to download.
 */
export const VideoSubtitleOverlay = ({
  debugName,
  video,
  getCurrentTime,
  enabled,
  maxHeight,
  style
}: VideoSubtitleOverlayProps) => {
  const { track, isRtl, text } = useVideoSubtitles({ video, getCurrentTime, enabled })

  if (!track) return null

  return (
    <TranslationOverlay debugName={debugName} text={text ?? ''} isRtl={isRtl} maxHeight={maxHeight} style={style} />
  )
}
